import React, { useEffect, useState } from "react";
import SidebarOrganizer from "../../components/Sidebar/SidebarOrganizer";
import { db, auth } from "../../firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { Wheel } from "react-custom-roulette";
import {
  calculateWinnerPayout,
  calculateMonthlyPool,
} from "../../utils/chitFundCalculations";
import "./OrganizerDashboard.css";

export default function Spinner() {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [winner, setWinner] = useState(null);
  const [manualPick, setManualPick] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);

  /* ================= GROUPS (LIVE) ================= */
  useEffect(() => {
    if (!auth.currentUser) return;

    const q = query(
      collection(db, "groups"),
      where("organizerId", "==", auth.currentUser.uid)
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setGroups(data);
      if (!selectedGroup && data.length > 0) setSelectedGroup(data[0]);
    });

    return () => unsub();
  }, [selectedGroup]);

  /* ================= MEMBERS (LIVE – FIXED) ================= */
  useEffect(() => {
    if (!selectedGroup) return;

    const membersRef = collection(
      db,
      `groups/${selectedGroup.id}/members`
    );

    const unsub = onSnapshot(membersRef, (snap) => {
      const data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setMembers(data);

      const firstEligible = data.find((m) => !m.hasWon);
      if (firstEligible) setManualPick(firstEligible.name);
    });

    return () => unsub();
  }, [selectedGroup]);

  /* ================= ELIGIBLE MEMBERS ================= */
  const eligibleMembers = members.filter((m) => !m.hasWon);

  const wheelData =
    eligibleMembers.length > 0
      ? eligibleMembers.map((m) => ({ option: m.name }))
      : [{ option: "Cycle Complete" }];

  /* ================= SPIN ================= */
  const spinForWinner = () => {
    if (eligibleMembers.length === 0) return;
    setShowConfirm(true);
  };

  const confirmSpin = () => {
    setShowConfirm(false);
    const index = Math.floor(Math.random() * eligibleMembers.length);
    setPrizeNumber(index);
    setMustSpin(true);
  };

  /* ================= CORE WINNER LOGIC ================= */
  const finalizeWinner = async (winnerMember) => {
    setWinner(winnerMember.name);
    setMustSpin(false);

    // 1️⃣ Mark winner
    await updateDoc(
      doc(
        db,
        `groups/${selectedGroup.id}/members/${winnerMember.id}`
      ),
      {
        hasWon: true,
        wonAt: serverTimestamp(),
        wonMonth: selectedGroup.currentMonth || 1,
      }
    );

    const winnersCount = members.filter((m) => m.hasWon).length + 1;
    const cycleComplete = winnersCount === members.length;

    // 2️⃣ RESET ALL MEMBERS IF CYCLE COMPLETED
    if (cycleComplete) {
      await Promise.all(
        members.map((m) =>
          updateDoc(
            doc(db, `groups/${selectedGroup.id}/members/${m.id}`),
            { hasWon: false }
          )
        )
      );
    }

    // 3️⃣ FINANCIAL CALCULATION
    const baseCollection =
      (selectedGroup.maxMembers || 0) *
      (selectedGroup.contribution || 0);

    const monthlyPool = calculateMonthlyPool(
      baseCollection,
      winnersCount - 1
    );

    const commission = selectedGroup.commission || 0;
    const payout = calculateWinnerPayout(monthlyPool, commission);

    // 4️⃣ UPDATE GROUP
    await updateDoc(doc(db, "groups", selectedGroup.id), {
      lastWinner: winnerMember.name,
      lastWinnerId: winnerMember.id,
      currentMonth: cycleComplete
        ? 1
        : (selectedGroup.currentMonth || 1) + 1,
      monthlyPoolAmount: monthlyPool,
      winnerPayout: payout,
      commissionAmount: commission,
      lastWinnerSelectedAt: serverTimestamp(),
    });

    alert(
      `✅ Winner: ${winnerMember.name}\n💰 Payout: ₹${payout}\n📅 Month: ${
        selectedGroup.currentMonth || 1
      }`
    );
  };

  const handleSpinStop = async () => {
    const winnerMember = eligibleMembers[prizeNumber];
    if (!winnerMember) return;
    await finalizeWinner(winnerMember);
  };

  /* ================= MANUAL PICK ================= */
  const confirmManualPick = async () => {
    const winnerMember = eligibleMembers.find(
      (m) => m.name === manualPick
    );
    if (!winnerMember) return;
    await finalizeWinner(winnerMember);
  };

  /* ================= UI (UNCHANGED) ================= */
  return (
    <div className="dashboard-layout">
      <SidebarOrganizer />

      <div className="organizer-dashboard">
        <h2>🎉 Winner Selection</h2>

        <div style={{ marginBottom: 20 }}>
          <b>Select Group:</b>
          <select
            value={selectedGroup?.id || ""}
            onChange={(e) =>
              setSelectedGroup(
                groups.find((g) => g.id === e.target.value)
              )
            }
            style={{ marginLeft: 10, padding: 6 }}
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.groupName}
              </option>
            ))}
          </select>
        </div>

        <div style={{ background: "#fff", padding: 32, borderRadius: 16 }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            {eligibleMembers.length > 0 ? (
              <Wheel
                mustStartSpinning={mustSpin}
                prizeNumber={prizeNumber}
                data={wheelData}
                backgroundColors={[
                  "#6366f1",
                  "#60a5fa",
                  "#a7f3d0",
                  "#fbbf24",
                ]}
                textColors={["#fff"]}
                onStopSpinning={handleSpinStop}
                fontSize={18}
              />
            ) : (
              <h3 style={{ color: "#6366f1" }}>
                Cycle Completed – Resetting…
              </h3>
            )}
          </div>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <button
              className="accept-btn"
              onClick={spinForWinner}
              disabled={eligibleMembers.length === 0}
            >
              Spin for Winner
            </button>
          </div>
        </div>

        <div style={{ marginTop: 24, background: "#fff", padding: 24 }}>
          <b>Manual Pick</b>
          <br />
          <select
            value={manualPick}
            onChange={(e) => setManualPick(e.target.value)}
            disabled={eligibleMembers.length === 0}
          >
            {eligibleMembers.map((m) => (
              <option key={m.id} value={m.name}>
                {m.name}
              </option>
            ))}
          </select>
          <button
            className="accept-btn"
            style={{ marginLeft: 12 }}
            onClick={confirmManualPick}
            disabled={eligibleMembers.length === 0}
          >
            Confirm Pick
          </button>
        </div>

        {winner && (
          <div style={{ marginTop: 20, color: "#059669", fontWeight: 600 }}>
            🎉 Winner Selected: {winner}
          </div>
        )}

        {showConfirm && (
          <div className="modal-backdrop">
            <div className="modal">
              <h3>Confirm Spin</h3>
              <p>This action cannot be undone.</p>
              <button
                className="reject-btn"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button className="accept-btn" onClick={confirmSpin}>
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
